import { useCallback, useContext, useMemo } from 'react';
import { EventAccordionItemBodyComponentType } from 'src/components/Accordions';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext/ModalContext';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { IconsFill, MoreIcon } from 'src/svgs/Viagogo';
import { ProceedsCell } from 'src/tables/SalesTable/configs/ProceedsCell';
import { SeatInfoContainer } from 'src/tables/Table';
import { DateCell, InHandCell } from 'src/tables/Table/TableCells';
import { ContentId } from 'src/utils/constants/contentId';
import { getCurrencyNumberFormat } from 'src/utils/format';
import {
  getSaleActionButton,
  getSaleDetailsModalConfigWithDeepLink,
  getSaleStatusDisplayContentId,
  NON_MAIN_SALE_ACTIONS,
} from 'src/utils/saleUtils';
import { getTicketTypeIcon } from 'src/utils/ticketTypeUtils';
import {
  Feature,
  ListingDetailDataField,
  Permission,
  PointOfSaleSaleStatus,
  Sale,
  SaleDetails,
  TicketType,
} from 'src/WebApiController';

// Import vanilla-extract components
import { TileHeader, TileLeftLayout, TileRightLayout } from '../Tile/Tile';
import {
  DetailLabel,
  OtherDetailsDiv,
  Price,
  SaleEventTileWrapper,
  SaleTilesContainer,
  Status,
} from './SaleTile.components';
import * as styles from './SaleTiles.css';

const SaleTile = ({
  sale,
  hasMobileSortSalesAndTicketProceeds,
}: {
  sale: Sale;
  hasMobileSortSalesAndTicketProceeds: boolean;
}) => {
  const ticketTypeIcon = getTicketTypeIcon(
    sale?.ticketType as TicketType,
    IconsFill.textPrimary
  );

  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();
  const { setModal } = useContext(ModalContext);
  const onViewClick = useCallback(async () => {
    setActivePosEntity(sale.id, sale.idOnMkp, true, [
      ListingDetailDataField.Basic,
    ]);
    setModal(getSaleDetailsModalConfigWithDeepLink(sale.id, true));
  }, [sale.idOnMkp, setActivePosEntity, setModal, sale.id]);

  const hasTotalNetProceedsPermissions = useUserHasAnyOfPermissions(
    Permission.Sales_ViewProceeds,
    Permission.Sales_ViewRecentProceeds
  );

  const hideOfflineActions = useUserHasFeature(Feature.RetailSale);

  const mainActionTypes = useMemo(
    () => sale.actions.filter((a) => !NON_MAIN_SALE_ACTIONS.includes(a)),
    [sale.actions]
  );

  const perTktProceeds = useMemo(() => {
    if (!hasMobileSortSalesAndTicketProceeds) return undefined;
    if (!sale.ttlNetProcs || !sale.qtySold) return undefined;
    const amt = sale.ttlNetProcs.amt / sale.qtySold;
    return getCurrencyNumberFormat(amt, sale.currency ?? undefined).format(amt);
  }, [
    hasMobileSortSalesAndTicketProceeds,
    sale.ttlNetProcs,
    sale.qtySold,
    sale.currency,
  ]);

  const actions = sale
    ? sale.status === PointOfSaleSaleStatus.PendingConfirmation
      ? [
          getSaleActionButton(
            sale.id,
            sale.idOnMkp,
            sale.mkp,
            sale.status,
            mainActionTypes[0],
            sale.ticketType,
            sale.isNoFulfill,
            'text',
            false,
            undefined,
            undefined,
            hideOfflineActions
          ),
          getSaleActionButton(
            sale.id,
            sale.idOnMkp,
            sale.mkp,
            sale.status,
            mainActionTypes[1],
            sale.ticketType,
            sale.isNoFulfill,
            'text',
            false,
            undefined,
            undefined,
            hideOfflineActions
          ),
        ]
      : mainActionTypes.length > 0
        ? [
            getSaleActionButton(
              sale.id,
              sale.idOnMkp,
              sale.mkp,
              sale.status,
              mainActionTypes[mainActionTypes.length - 1],
              sale.ticketType,
              sale.isNoFulfill,
              'text',
              false,
              undefined,
              undefined,
              hideOfflineActions
            ),
          ]
        : []
    : [];

  return (
    <SaleEventTileWrapper onClick={onViewClick}>
      <TileHeader>
        <TileLeftLayout>
          <div className={styles.detailsDiv}>
            {ticketTypeIcon}
            <SeatInfoContainer>
              <SeatingInfo
                {...sale?.seating}
                isSeatSaver={sale?.isSeatSaver}
                adCampaignFlightId={sale?.adCmpFlightId}
              />
            </SeatInfoContainer>
          </div>
        </TileLeftLayout>
        <TileRightLayout columns={2}>
          {hasMobileSortSalesAndTicketProceeds ? (
            <DateCell date={sale.saleDate} align="left" enableUtcFallback />
          ) : (
            <InHandCell
              date={sale.inHandAt ? new Date(sale.inHandAt) : null}
              needsToFulfill={Boolean(
                sale.status &&
                ![
                  PointOfSaleSaleStatus.ProcessingFulfillment,
                  PointOfSaleSaleStatus.Fulfilled,
                  PointOfSaleSaleStatus.CancelledHold,
                  PointOfSaleSaleStatus.Rejected,
                  PointOfSaleSaleStatus.PendingRejection,
                ].includes(sale.status)
              )}
              handleFulfillmentState
            />
          )}
          <span>
            {actions.length > 0 ? (
              <PosDropdown
                trigger={
                  <MoreIcon
                    withHoverEffect
                    fill={IconsFill.textBrand}
                    align="middle"
                  />
                }
                align="end"
              >
                {actions.map((a, i) => (
                  <PosDropdownItem key={i}>{a}</PosDropdownItem>
                ))}
              </PosDropdown>
            ) : null}
          </span>
        </TileRightLayout>
      </TileHeader>
      <div className={styles.saleBody}>
        <TileLeftLayout>
          <OtherDetailsDiv>
            <Status>
              <Content
                id={getSaleStatusDisplayContentId(
                  sale.status,
                  sale.mkpContrState,
                  sale.mkp
                )}
              />
            </Status>
            {!hasMobileSortSalesAndTicketProceeds && (
              <DateCell date={sale.saleDate} align="left" enableUtcFallback />
            )}
          </OtherDetailsDiv>
        </TileLeftLayout>
        <TileRightLayout columns={1}>
          {hasTotalNetProceedsPermissions &&
            (hasMobileSortSalesAndTicketProceeds ? (
              <div>
                <DetailLabel textAlign="right">
                  <Content id={ContentId.ProceedsPerTicket} />
                </DetailLabel>
                <Price>
                  <ProceedsCell
                    displayValue={perTktProceeds}
                    margin={undefined}
                  />
                </Price>
              </div>
            ) : (
              <Price>
                <ProceedsCell
                  displayValue={sale?.ttlNetProcs?.disp ?? undefined}
                  margin={undefined}
                />
              </Price>
            ))}
        </TileRightLayout>
      </div>
    </SaleEventTileWrapper>
  );
};

type EventSaleBodyProps = EventAccordionItemBodyComponentType<Sale>;

export const SaleTiles = ({ entities }: EventSaleBodyProps) => {
  const hasMobileSortSalesAndTicketProceeds = useUserHasFeature(
    Feature.MobileSortSalesAndTicketProceeds
  );

  const sortedEntities = useMemo(
    () =>
      entities && hasMobileSortSalesAndTicketProceeds
        ? [...entities].sort(
            (a, b) =>
              new Date(b.saleDate).getTime() - new Date(a.saleDate).getTime()
          )
        : entities,
    [entities, hasMobileSortSalesAndTicketProceeds]
  );

  return sortedEntities ? (
    <SaleTilesContainer>
      {sortedEntities.map((sale) => (
        <SaleTile
          key={sale.id}
          sale={sale}
          hasMobileSortSalesAndTicketProceeds={
            hasMobileSortSalesAndTicketProceeds
          }
        />
      ))}
    </SaleTilesContainer>
  ) : (
    <PosSpinner size={vars.iconSize.s} />
  );
};
